import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import {
  submitToFACe,
  checkFACeStatus,
  mapFACeEstadoToInternal,
} from '@/lib/invoicing/face-service';

export default withAuth(async (req, res, user) => {
  if (req.method === 'POST') {
    return handleSubmit(req, res);
  }

  if (req.method === 'GET') {
    return handleStatusCheck(req, res);
  }

  return res.status(405).json({ error: 'Método no permitido' });
}, ['admin', 'asesor']);

async function handleSubmit(
  req: NextApiRequest,
  res: NextApiResponse
): Promise<void | NextApiResponse> {
  try {
    const {
      invoice_id,
      oficina_contable,
      organo_gestor,
      unidad_tramitadora,
    } = req.body;

    if (!invoice_id) {
      return res.status(400).json({ error: 'invoice_id es requerido' });
    }

    if (!oficina_contable || !organo_gestor || !unidad_tramitadora) {
      return res.status(400).json({
        error: 'oficina_contable, organo_gestor y unidad_tramitadora son requeridos para FACe',
      });
    }

    const { data: invoice, error: fetchError } = await supabaseAdmin
      .from('invoices')
      .select('*')
      .eq('id', invoice_id)
      .single();

    if (fetchError || !invoice) {
      return res.status(404).json({ error: 'Factura no encontrada' });
    }

    if (invoice.estado === 'borrador') {
      return res.status(400).json({
        error: 'La factura debe estar emitida antes de enviarla a FACe',
      });
    }

    if (invoice.face_enviada && invoice.face_estado === 'aceptada') {
      return res.status(400).json({
        error: 'La factura ya ha sido aceptada por FACe',
        face_numero_registro: invoice.face_numero_registro,
        face_estado: invoice.face_estado,
      });
    }

    // FACe requires Facturae XML — attempt to generate or retrieve it
    let facturaeXml = '';

    try {
      const { generateFacturaeXml } = await import('@/lib/invoicing/facturae-generator');
      const { data: lines } = await supabaseAdmin
        .from('invoice_lines')
        .select('*')
        .eq('invoice_id', invoice_id)
        .order('orden', { ascending: true });

      if (lines && lines.length > 0) {
        facturaeXml = generateFacturaeXml({ ...invoice, lines }, invoice);
      }
    } catch {
      console.warn('[API face] Facturae generator not available, using placeholder XML');
      facturaeXml = `<!-- STUB: Facturae XML for invoice ${invoice.numero} -->`;
    }

    if (!facturaeXml) {
      return res.status(400).json({
        error: 'No se pudo generar el XML de Facturae necesario para FACe',
      });
    }

    const result = await submitToFACe({
      facturae_xml: facturaeXml,
      oficina_contable,
      organo_gestor,
      unidad_tramitadora,
    });

    if (!result.success) {
      return res.status(400).json({
        error: result.error?.descripcion || 'Error al enviar a FACe',
        face_error: result.error,
      });
    }

    const faceEstadoInternal = result.estado
      ? mapFACeEstadoToInternal(result.estado)
      : 'pendiente';

    const updateData: Record<string, unknown> = {
      face_enviada: true,
      face_estado: faceEstadoInternal,
      updated_at: new Date().toISOString(),
    };

    if (result.numeroRegistro) {
      updateData.face_numero_registro = result.numeroRegistro;
    }

    const { error: updateError } = await supabaseAdmin
      .from('invoices')
      .update(updateData)
      .eq('id', invoice_id);

    if (updateError) {
      console.error('[API face] Error updating invoice after FACe submission:', updateError);
      return res.status(500).json({
        error: 'Envío realizado pero error al actualizar la factura',
        face_result: result,
      });
    }

    return res.status(200).json({
      success: true,
      face_numero_registro: result.numeroRegistro,
      face_estado: faceEstadoInternal,
      face_estado_descripcion: result.estadoDescripcion,
      fecha_registro: result.fechaRegistro,
    });
  } catch (error: any) {
    console.error('[API face] Unexpected error:', error);
    return res.status(500).json({
      error: error.message || 'Error interno del servidor',
    });
  }
}

async function handleStatusCheck(
  req: NextApiRequest,
  res: NextApiResponse
): Promise<void | NextApiResponse> {
  try {
    const invoice_id = req.query.invoice_id as string;

    if (!invoice_id) {
      return res.status(400).json({ error: 'invoice_id query param es requerido' });
    }

    const { data: invoice, error: fetchError } = await supabaseAdmin
      .from('invoices')
      .select('id, numero, face_enviada, face_numero_registro, face_estado')
      .eq('id', invoice_id)
      .single();

    if (fetchError || !invoice) {
      return res.status(404).json({ error: 'Factura no encontrada' });
    }

    if (!invoice.face_enviada || !invoice.face_numero_registro) {
      return res.status(200).json({
        success: true,
        face_enviada: false,
        face_estado: null,
        message: 'La factura no ha sido enviada a FACe',
      });
    }

    const statusResult = await checkFACeStatus(invoice.face_numero_registro);

    if (statusResult.success && statusResult.estado) {
      const newEstado = mapFACeEstadoToInternal(statusResult.estado);

      if (newEstado !== invoice.face_estado) {
        await supabaseAdmin
          .from('invoices')
          .update({
            face_estado: newEstado,
            updated_at: new Date().toISOString(),
          })
          .eq('id', invoice_id);
      }

      return res.status(200).json({
        success: true,
        face_enviada: true,
        face_numero_registro: invoice.face_numero_registro,
        face_estado: newEstado,
        face_estado_descripcion: statusResult.estadoDescripcion,
        face_motivo: statusResult.motivo,
        face_tramitacion: statusResult.tramitacion,
      });
    }

    return res.status(200).json({
      success: true,
      face_enviada: true,
      face_numero_registro: invoice.face_numero_registro,
      face_estado: invoice.face_estado,
    });
  } catch (error: any) {
    console.error('[API face] Status check error:', error);
    return res.status(500).json({
      error: error.message || 'Error interno del servidor',
    });
  }
}
